import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BookOpen, Eye, EyeOff, Library, Loader2, Mail, Send } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiGet, apiPost, apiPut } from '@/lib/api';

/** School syllabus library + admin-managed Resend key + weekly digest trigger. */
export function LibraryIntegrationsPanel() {
  const [library, setLibrary] = useState({ items: [], schools: [] });
  const [loading, setLoading] = useState(false);
  const [integrations, setIntegrations] = useState(null);
  const [form, setForm] = useState({ api_key: '', from_email: '', from_name: 'GurukulVeda' });
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lib, integ] = await Promise.all([
        apiGet('/admin/syllabus-library'),
        apiGet('/admin/integrations'),
      ]);
      setLibrary(lib);
      setIntegrations(integ.resend);
      setForm(f => ({ ...f, from_email: integ.resend?.from_email || '', from_name: integ.resend?.from_name || 'GurukulVeda' }));
    } catch (e) { toast.error(e?.message || 'Could not load this panel'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await apiPut(`/admin/syllabus-library/${id}/status`, { status });
      toast.success(status === 'published' ? 'Published to the school' : 'Hidden from families');
      load();
    } catch (e) { toast.error(e?.message || 'Could not change that'); }
  };

  const saveResend = async () => {
    setBusy('save');
    try {
      const res = await apiPut('/admin/integrations/resend', form);
      setIntegrations(res.resend);
      setForm(f => ({ ...f, api_key: '' }));
      toast.success('Email settings saved');
    } catch (e) { toast.error(e?.message || 'Could not save that key'); }
    setBusy('');
  };

  const sendTest = async () => {
    setBusy('test');
    try {
      const res = await apiPost('/admin/integrations/resend/test', { to_email: testTo });
      toast.success(res.message || 'Test sent');
      load();
    } catch (e) { toast.error(e?.message || 'Test send failed'); }
    setBusy('');
  };

  const runDigest = async () => {
    setBusy('digest');
    try {
      const res = await apiPost('/admin/digest/run-weekly', {});
      toast.success(`Digest run — ${res.nudges} in-app nudge(s), ${res.emails} email(s)`);
    } catch (e) { toast.error(e?.message || 'Could not run the digest'); }
    setBusy('');
  };

  return (
    <div className="space-y-6" data-testid="admin-library-integrations">
      <Card className="p-6 rounded-2xl border-slate-100">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
              <Library className="w-4 h-4 text-indigo-500" /> Syllabus Library
            </h3>
            <p className="text-xs text-slate-500 font-body mt-0.5">
              Approved family uploads become their school's syllabus — the next child from that school
              just confirms it instead of uploading anything.
            </p>
          </div>
          <Button size="sm" variant="outline" className="h-8 text-[11px]" data-testid="library-refresh" onClick={load}>
            Refresh
          </Button>
        </div>

        {loading && <p className="mt-4 text-sm text-slate-400 font-body"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</p>}

        {!loading && library.items.length === 0 && (
          <p className="mt-4 text-sm text-slate-500 font-body" data-testid="library-empty">
            Nothing published yet. Approve a family syllabus upload under Schools → submissions and it lands here.
          </p>
        )}

        <div className="mt-4 space-y-2">
          {library.items.map(item => (
            <div key={item.library_id} data-testid={`library-row-${item.library_id}`}
              className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl border border-slate-100 p-3">
              <div className="min-w-0 flex-1">
                <p className="font-heading text-[13px] font-semibold text-slate-800 truncate">
                  {item.school}
                </p>
                <p className="text-[11px] text-slate-500 font-body">
                  {item.board?.toUpperCase()} · Class {item.class_level} · {item.subject} ·
                  {' '}{item.counts.chapters} chapters, {item.counts.topics} topics
                  {item.city ? ` · ${item.city}` : ''}
                </p>
              </div>
              <span className={`text-[10px] font-heading uppercase px-2 py-0.5 rounded-full ${item.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                {item.status}
              </span>
              {item.status === 'published' ? (
                <Button size="sm" variant="outline" className="h-7 text-[10px]" data-testid={`library-hide-${item.library_id}`}
                  onClick={() => setStatus(item.library_id, 'hidden')}>
                  <EyeOff className="w-3 h-3 mr-1" /> Hide
                </Button>
              ) : (
                <Button size="sm" variant="outline" className="h-7 text-[10px]" data-testid={`library-publish-${item.library_id}`}
                  onClick={() => setStatus(item.library_id, 'published')}>
                  <Eye className="w-3 h-3 mr-1" /> Publish
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-slate-100">
        <h3 className="font-heading text-base font-semibold text-slate-800 flex items-center gap-2">
          <Mail className="w-4 h-4 text-sky-500" /> Email (Resend)
        </h3>
        <p className="text-xs text-slate-500 font-body mt-0.5">
          Used for the weekly parent digest. The key is stored server-side and never shown again.
          {integrations?.configured && (integrations.verified
            ? ' Verified and live.'
            : ' Saved — send a test to verify it before parents see the email option.')}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
          <div>
            <Label className="text-xs font-body text-slate-500">Resend API key</Label>
            <Input data-testid="resend-key-input" type="password" placeholder="re_..." value={form.api_key}
              onChange={e => setForm(f => ({ ...f, api_key: e.target.value }))} className="mt-1 rounded-lg text-sm" />
          </div>
          <div>
            <Label className="text-xs font-body text-slate-500">Send from</Label>
            <Input data-testid="resend-from-input" placeholder="digest@yourdomain.com" value={form.from_email}
              onChange={e => setForm(f => ({ ...f, from_email: e.target.value }))} className="mt-1 rounded-lg text-sm" />
          </div>
          <div>
            <Label className="text-xs font-body text-slate-500">Sender name</Label>
            <Input data-testid="resend-name-input" value={form.from_name}
              onChange={e => setForm(f => ({ ...f, from_name: e.target.value }))} className="mt-1 rounded-lg text-sm" />
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-2 mt-3">
          <Button size="sm" className="h-8 text-[11px] bg-slate-900 text-white" disabled={busy === 'save'}
            data-testid="resend-save-btn" onClick={saveResend}>
            {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null} Save key
          </Button>
          <div className="flex items-end gap-1.5">
            <Input data-testid="resend-test-input" placeholder="test@you.com" value={testTo}
              onChange={e => setTestTo(e.target.value)} className="h-8 w-48 rounded-lg text-sm" />
            <Button size="sm" variant="outline" className="h-8 text-[11px]" disabled={busy === 'test' || !testTo}
              data-testid="resend-test-btn" onClick={sendTest}>
              <Send className="w-3.5 h-3.5 mr-1" /> Test send
            </Button>
          </div>
          <Button size="sm" variant="outline" className="h-8 text-[11px]" disabled={busy === 'digest'}
            data-testid="digest-run-btn" onClick={runDigest}>
            <BookOpen className="w-3.5 h-3.5 mr-1" /> Run weekly digest now
          </Button>
        </div>
        {integrations?.last_test_at && (
          <p className="text-[10px] text-slate-400 font-body mt-2">Last test send: {new Date(integrations.last_test_at).toLocaleString('en-IN')}</p>
        )}
      </Card>
    </div>
  );
}

export default LibraryIntegrationsPanel;
